import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from 'react'
import { startPhoneOtp, checkPhoneOtp, setPhoneSession, ApiError } from '../api'
import Turnstile, { isTurnstileConfigured, type TurnstileStatus } from '../components/Turnstile'
import { OtpInput } from '../components/OtpInput'
import { formatPhoneDisplay } from '@parkify/account'

/**
 * Portal sign-in. Phone OTP (inline SMS code via Twilio Verify — the same flow
 * as the consumer checkout) is the only sign-in path. On success, onSignedIn()
 * bounces the App router to Home.
 */
export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  return <PhoneSignIn onSignedIn={onSignedIn} />
}

const labelStyle: CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: 1.2,
  color: 'var(--color-gray-700)', marginBottom: 8,
}

const errorStyle: CSSProperties = {
  margin: '12px 0 0',
  color: 'var(--color-error)',
  background: 'var(--color-error-bg)',
  border: '1px solid rgba(237, 28, 36, 0.2)',
  borderRadius: 'var(--radius-sm)',
  padding: '10px 12px',
  fontSize: 13,
}

// ─── Phone (primary) ─────────────────────────────────────────────────────────

function PhoneSignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'enter' | 'code'>('enter')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Turnstile (SMS-pumping defense). Invisible + auto-solving for genuine users;
  // Cloudflare shows an interactive challenge only when it FLAGS the visitor
  // (common on shared/cellular mobile IPs). Enforcement is LIVE server-side on
  // /phone/start: a send WITHOUT a valid token is HARD-rejected (403
  // turnstile_required) — it only fails open on a siteverify network error, never
  // on a missing token. The widget auto-solves in ~1s, but the send button was
  // enabled immediately, so a fast tap fired a tokenless request that 403'd — the
  // "have to press Text me a code twice" bug. Fix: a send requested before the
  // token is ready is held PENDING and auto-fired the instant the token lands, so
  // the user never taps twice. We still fail OPEN (fire anyway) on a widget error
  // or after a short grace window, so a genuine sign-in is NEVER stuck behind a
  // slow/broken challenge. Single-use — bump the nonce after each send (also
  // covers "Resend code") to remount for a fresh token.
  const [turnstileToken, setTurnstileToken] = useState('')
  const [turnstileNonce, setTurnstileNonce] = useState(0)
  const [turnstileStatus, setTurnstileStatus] = useState<TurnstileStatus | null>(null)
  // A send was requested but is waiting on the Turnstile token to resolve.
  const [pendingSend, setPendingSend] = useState(false)

  const turnstileOn = isTurnstileConfigured()
  // OK to fire /phone/start now: Turnstile isn't in play, we already hold a token,
  // or the widget errored (fail open — never hard-block a real sign-in).
  const canSendNow = !turnstileOn || !!turnstileToken || turnstileStatus === 'error'
  // A visible challenge is up and unsolved — wait on the human (drives the hint).
  const awaitingChallenge = turnstileStatus === 'interactive' && !turnstileToken
  const working = busy || pendingSend

  const phoneValid = phone.replace(/\D/g, '').length >= 10
  const codeValid = /^\d{4,10}$/.test(code.trim())

  const doSend = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      await startPhoneOtp(phone, turnstileToken || undefined)
      setTurnstileToken('')
      setTurnstileStatus(null) // fresh widget re-signals its own status
      setTurnstileNonce((n) => n + 1)
      setCode('')
      setStep('code')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send a code. Try again.')
    } finally {
      setBusy(false)
    }
  }, [phone, turnstileToken])

  // User asked to send. If the token's ready, go now; otherwise mark it pending —
  // the effects below fire it the moment the token resolves (no second tap).
  const requestSend = (e: FormEvent) => {
    e.preventDefault()
    if (working || !phoneValid) return
    setError(null)
    if (canSendNow) void doSend()
    else setPendingSend(true)
  }

  // Fire a pending send the instant the token is ready (or the widget failed open).
  useEffect(() => {
    if (pendingSend && canSendNow) {
      setPendingSend(false)
      void doSend()
    }
  }, [pendingSend, canSendNow, doSend])

  // Fail-open grace window: if the invisible widget hasn't produced a token in a
  // few seconds (and no visible challenge is waiting on the user), send anyway so
  // a slow/blocked challenge never traps a genuine sign-in. Skipped while an
  // interactive challenge is on screen — that needs the human, not a timer.
  useEffect(() => {
    if (!pendingSend || awaitingChallenge) return
    const t = setTimeout(() => { setPendingSend(false); void doSend() }, 5000)
    return () => clearTimeout(t)
  }, [pendingSend, awaitingChallenge, doSend])

  // Only the invisible auto-solve wait shows a spinner; a visible challenge keeps
  // the label and leans on the hint below so the user knows to complete it.
  const showSpinner = busy || (pendingSend && !awaitingChallenge)

  // `raw` lets the OtpInput auto-submit-on-complete pass the freshly-filled code
  // directly, so it never races the `code` state update (matches the consumer).
  const submitCode = async (raw?: string) => {
    const c = (raw ?? code).trim()
    if (busy || !/^\d{4,10}$/.test(c)) return // guards the auto-submit + manual double-fire
    setBusy(true)
    setError(null)
    try {
      const res = await checkPhoneOtp(phone, c)
      setPhoneSession(res.token, res.phone)
      onSignedIn()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That code is incorrect or has expired.')
    } finally {
      setBusy(false)
    }
  }

  const verify = (e: FormEvent) => {
    e.preventDefault()
    void submitCode()
  }

  if (step === 'code') {
    return (
      <div className="card">
        <h1 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 8px', letterSpacing: -0.4 }}>
          Enter your code
        </h1>
        <p style={{ margin: '0 0 24px', color: 'var(--color-gray-700)', lineHeight: 1.6 }}>
          We texted a sign-in code to <strong style={{ color: 'var(--color-black)' }}>{formatPhoneDisplay(phone)}</strong>.
        </p>
        <form onSubmit={verify}>
          <label htmlFor="otp" style={labelStyle}>Verification code</label>
          <OtpInput
            id="otp"
            value={code}
            onChange={v => { setCode(v); setError(null) }}
            onComplete={full => void submitCode(full)}
            error={!!error}
            autoFocus
            disabled={busy}
            aria-label="6-digit verification code"
          />
          {error && <p style={errorStyle}>{error}</p>}
          {turnstileOn && (
            <Turnstile onToken={setTurnstileToken} onStatus={setTurnstileStatus} refreshKey={turnstileNonce} />
          )}
          {awaitingChallenge && (
            <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--color-gray-600)', textAlign: 'center' }}>
              Complete the verification above to resend a code.
            </p>
          )}
          {/* Verify is NOT a send — it checks the SMS code, so it's not gated on
              Turnstile (which defends the send path). Only "Resend code" is. */}
          <button
            type="submit"
            className="btn-primary"
            style={{ marginTop: 20, width: '100%' }}
            disabled={busy || !codeValid}
          >
            {busy ? <span className="spinner" /> : 'Verify & sign in'}
          </button>
        </form>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 18 }}>
          <button type="button" className="btn-text" onClick={requestSend} disabled={working}>Resend code</button>
          <button type="button" className="btn-text" onClick={() => { setStep('enter'); setError(null) }} disabled={busy}>
            Change number
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <h1 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 8px', letterSpacing: -0.4 }}>
        Sign in to your account
      </h1>
      <p style={{ margin: '0 0 24px', color: 'var(--color-gray-700)', lineHeight: 1.6 }}>
        Enter your mobile number and we'll text you a sign-in code. It's the number you used when you parked.
      </p>
      <form onSubmit={requestSend}>
        <label htmlFor="phone" style={labelStyle}>Mobile number</label>
        <input
          id="phone"
          className="text-field"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          autoFocus
          placeholder="(555) 123-4567"
          value={phone}
          onChange={e => { setPhone(e.target.value); setError(null) }}
          disabled={busy}
        />
        {error && <p style={errorStyle}>{error}</p>}
        {turnstileOn && (
          <Turnstile onToken={setTurnstileToken} onStatus={setTurnstileStatus} refreshKey={turnstileNonce} />
        )}
        {awaitingChallenge && (
          <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--color-gray-600)', textAlign: 'center' }}>
            Complete the verification above to continue.
          </p>
        )}
        <button
          type="submit"
          className="btn-primary"
          style={{ marginTop: 20, width: '100%' }}
          disabled={working || !phoneValid}
        >
          {showSpinner ? <span className="spinner" /> : 'Text me a code'}
        </button>
      </form>
    </div>
  )
}
